import { useApi } from "./api-service";
import {
  ISubscriptionsResponse,
  ISubscriptionBackend,
  ISubscriptionFrontend,
  IUpdateSubscriptionPaymentRequest,
  IUpdateSubscriptionPaymentResponse,
  ICreateCheckoutSessionSetupResponse,
  ICheckoutSessionDetails,
} from "../../types/api/billing";

// Helper to map backend subscription to frontend shape extracting agent info
function mapSubscription(sub: ISubscriptionBackend): ISubscriptionFrontend {
  const agentId = sub.metadata?.agentId || sub.metadata?.agentID; // tolerate slight variations
  const agentName = sub.description || undefined;
  // Flatten: include only the fields specified in ISubscriptionBackend plus agent metadata
  return {
    id: sub.id,
    status: sub.status,
    customer: sub.customer,
    default_payment_method: sub.default_payment_method ?? null,
    items: sub.items,
    latest_invoice: sub.latest_invoice ?? null,
    invoices: sub.invoices ?? null,
    metadata: sub.metadata,
    description: sub.description,
    agentId,
    agentName,
  };
}

export function useBillingService() {
  // Billing endpoints are not agent-scoped so agentRequired = false
  const api = useApi(false);

  return {
    fetchSubscriptions: async (): Promise<ISubscriptionFrontend[]> => {
      const res = await api.get<ISubscriptionsResponse>(
        "/billing/subscriptions"
      );
      return res.subscriptions.map(mapSubscription);
    },

    updateSubscriptionPayment: async (
      subscriptionId: string,
      payload: IUpdateSubscriptionPaymentRequest
    ): Promise<IUpdateSubscriptionPaymentResponse> => {
      return api.post<IUpdateSubscriptionPaymentResponse>(
        `/billing/subscriptions/${subscriptionId}/payment`,
        payload
      );
    },

    // Embedded Checkout (setup mode)
    createEmbeddedCheckoutSetupSession: async (
      customerId: string
    ): Promise<ICreateCheckoutSessionSetupResponse> => {
      return api.post<ICreateCheckoutSessionSetupResponse>(
        "/billing/checkout-sessions/setup",
        { customerId }
      );
    },

    getCheckoutSession: async (
      id: string
    ): Promise<ICheckoutSessionDetails> => {
      return api.get<ICheckoutSessionDetails>(
        `/billing/checkout-sessions/${id}`
      );
    },
  };
}
